import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import BackOfficeLayout from "./BackOfficeLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  Loader2,
  TrendingUp,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackOfficeAnalytics = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/backoffice/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const statusConfigs = {
    pending: { label: "Pending", color: "bg-yellow-500", icon: Clock, textColor: "text-yellow-700", bgColor: "bg-yellow-50" },
    recommended: { label: "Recommended", color: "bg-indigo-500", icon: FileText, textColor: "text-indigo-700", bgColor: "bg-indigo-50" },
    escalated: { label: "Escalated", color: "bg-orange-500", icon: ArrowUpRight, textColor: "text-orange-700", bgColor: "bg-orange-50" },
    approved: { label: "Approved", color: "bg-green-500", icon: CheckCircle, textColor: "text-green-700", bgColor: "bg-green-50" },
    rejected: { label: "Rejected", color: "bg-red-500", icon: XCircle, textColor: "text-red-700", bgColor: "bg-red-50" }
  };

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </BackOfficeLayout>
    );
  }

  const totalAmount = stats?.amount_by_status?.reduce((sum, item) => sum + (item.total_amount || 0), 0) || 0;
  const approvedAmount = stats?.amount_by_status?.find(a => a._id === "approved")?.total_amount || 0;

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Overview of loan application metrics</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Applications</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_applications || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Requested</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved Amount</p>
                  <p className="text-3xl font-bold text-green-700">{formatCurrency(approvedAmount)}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-3xl font-bold text-yellow-700">
                    {(stats?.status_counts?.pending || 0) + (stats?.status_counts?.recommended || 0) + (stats?.status_counts?.escalated || 0)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(statusConfigs).map(([status, config]) => {
                  const count = stats?.status_counts?.[status] || 0;
                  const total = stats?.total_applications || 1;
                  const percentage = Math.round((count / total) * 100);
                  const Icon = config.icon;
                  
                  return (
                    <div key={status} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${config.textColor}`} />
                          <span className="text-sm font-medium">{config.label}</span>
                        </div>
                        <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${config.color} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Amount by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.amount_by_status?.map((item) => {
                  const config = statusConfigs[item._id] || { label: item._id, bgColor: "bg-gray-50", textColor: "text-gray-700" };
                  
                  return (
                    <div key={item._id} className={`p-4 rounded-lg ${config.bgColor}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm font-medium ${config.textColor}`}>{config.label}</p>
                          <p className="text-xs text-gray-500">{item.count} applications</p>
                        </div>
                        <p className={`text-lg font-bold ${config.textColor}`}>
                          {formatCurrency(item.total_amount)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Required */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg border-yellow-200 bg-yellow-50">
                <p className="text-sm text-yellow-700 font-medium">Awaiting Review</p>
                <p className="text-3xl font-bold text-yellow-800">{stats?.status_counts?.pending || 0}</p>
                <p className="text-xs text-yellow-600 mt-1">Need loan officer recommendation</p>
              </div>
              <div className="p-4 border rounded-lg border-indigo-200 bg-indigo-50">
                <p className="text-sm text-indigo-700 font-medium">Awaiting Manager</p>
                <p className="text-3xl font-bold text-indigo-800">{stats?.status_counts?.recommended || 0}</p>
                <p className="text-xs text-indigo-600 mt-1">Ready for manager decision</p>
              </div>
              <div className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                <p className="text-sm text-orange-700 font-medium">Awaiting Executive</p>
                <p className="text-3xl font-bold text-orange-800">{stats?.status_counts?.escalated || 0}</p>
                <p className="text-xs text-orange-600 mt-1">Escalated for executive approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeAnalytics;
